/**
 * Bot difficulty, and the one place that resolves it.
 *
 * ## Why skill and not health
 *
 * The cheap way to make bots hard is to give them more hit points, and it is
 * the wrong way: a soldier who eats two extra rifle rounds does not read as
 * skilled, it reads as broken, and it punishes good aim instead of rewarding
 * it. Every knob here is PERCEPTION or MARKSMANSHIP — how fast they notice
 * you, how long they take to bring the gun up, how tight the cone is, how much
 * ground they are willing to give. A Veteran bot dies to one headshot exactly
 * like a Recruit; it just sees you first and hits more of what it aims at.
 *
 * ## Which knobs, and which way round
 *
 * - `accuracy` MULTIPLIES the bot's own spread cone, so LOWER IS BETTER.
 *   `baseAccuracy` is 0.030-0.048 rad per bot, which stays as the per-bot
 *   variation — two Veterans are still not the same shooter.
 * - `reaction` multiplies the acquire delay before the first shot. This is the
 *   single most load-bearing number for how a fight FEELS: a bot that fires the
 *   instant it sees you is unfightable at any accuracy, and one that takes a
 *   second is free.
 * - `alertGain` is how fast suspicion builds while you are in view, `hearing`
 *   the radius gunfire pulls them in from, and `fov` the cosine limits of their
 *   attention cone. Perception is what separates "hard" from "cheating": a bot
 *   with a tight cone that has not noticed you yet is fair.
 * - `burstSpread` loosens only the FIRST burst after acquiring. Keeping it high
 *   at low difficulty is what makes an opening exchange survivable.
 * - `aggression` scales how far they will push and how readily they break
 *   cover; `suppressResist` how much incoming fire spoils their aim.
 *
 * Singleplayer only. Online the server owns the bots, and a client-side
 * difficulty setting there would be either a desync or a cheat.
 */
export const DIFFICULTIES = [
  {
    id: 'recruit',
    name: 'RECRUIT',
    sub: 'Learning the map',
    blurb: 'Slow to notice you, slower to shoot, and they miss the first '
      + 'burst. For getting to know a level without being punished for it.',
    accuracy: 2.1,
    reaction: 2.0,
    alertGain: 0.55,
    hearing: 28,
    fov: { idle: 0.55, alert: 0.10 },
    burstSpread: 3.0,
    aggression: 0.55,
    suppressResist: 0.5,
    speed: 0.88,
    maxRange: 45,
  },
  {
    id: 'regular',
    name: 'REGULAR',
    sub: 'The way it has always played',
    blurb: 'The original behaviour: about a third of a second to react, a '
      + 'loose opening burst and a fair attention cone.',
    accuracy: 1.0,
    reaction: 1.0,
    alertGain: 1.0,
    hearing: 55,
    fov: { idle: 0.15, alert: -0.5 },
    burstSpread: 2.0,
    aggression: 1.0,
    suppressResist: 1.0,
    speed: 1.0,
    maxRange: 75,
  },
  {
    id: 'hardened',
    name: 'HARDENED',
    sub: 'They shoot back properly',
    blurb: 'Half the reaction time and a noticeably tighter cone. They hold '
      + 'angles, use cover, and punish you for standing in the open.',
    accuracy: 0.62,
    reaction: 0.58,
    alertGain: 1.5,
    hearing: 70,
    fov: { idle: 0.0, alert: -0.7 },
    burstSpread: 1.5,
    aggression: 1.25,
    suppressResist: 1.4,
    speed: 1.04,
    maxRange: 85,
  },
  {
    id: 'veteran',
    name: 'VETERAN',
    sub: 'Punishes every mistake',
    blurb: 'Fast acquisition, tight groups, wide awareness and they push. '
      + 'Peeking the same angle twice will get you killed.',
    accuracy: 0.40,
    reaction: 0.34,
    alertGain: 2.1,
    hearing: 85,
    fov: { idle: -0.2, alert: -0.9 },
    burstSpread: 1.2,
    aggression: 1.5,
    suppressResist: 1.8,
    speed: 1.08,
    maxRange: 95,
  },
  {
    id: 'elite',
    name: 'ELITE',
    sub: 'Unreasonable, on purpose',
    blurb: 'Near-instant reaction and very little spread. Not balanced — it '
      + 'is here to be beaten.',
    accuracy: 0.26,
    reaction: 0.18,
    alertGain: 3.0,
    hearing: 100,
    fov: { idle: -0.4, alert: -1.0 },
    burstSpread: 1.0,
    aggression: 1.7,
    suppressResist: 2.4,
    speed: 1.12,
    maxRange: 110,
  },
];

export const DEFAULT_DIFFICULTY = 'regular';
const STORE_KEY = 'blackout.difficulty';

export function difficultyById(id) {
  return DIFFICULTIES.find((d) => d.id === id)
    ?? DIFFICULTIES.find((d) => d.id === DEFAULT_DIFFICULTY);
}

/** The player's saved choice, or the default. */
export function savedDifficulty() {
  try { return difficultyById(localStorage.getItem(STORE_KEY) ?? DEFAULT_DIFFICULTY).id; }
  catch { return DEFAULT_DIFFICULTY; }
}

export function saveDifficulty(id) {
  try { localStorage.setItem(STORE_KEY, id); } catch { /* private mode */ }
}

/**
 * Stamp a preset onto one bot.
 *
 * Called from the Enemy constructor AND again when the player changes the
 * setting mid-match, so it has to be idempotent — which is why it works off
 * `_skillBase`, the bot's own untouched roll, rather than compounding onto
 * whatever the last preset left behind. Applying a multiplier to an already
 * multiplied number is how a difficulty menu ends up drifting to one extreme
 * after a few changes.
 */
export function applySkill(enemy, id) {
  const d = difficultyById(id);
  const base = (enemy._skillBase ??= {
    accuracy: enemy.baseAccuracy,
    reaction: enemy.reactionTime,
    speeds: { ...enemy.speeds },
  });
  enemy.skill = d;
  enemy.baseAccuracy = base.accuracy * d.accuracy;
  enemy.reactionTime = base.reaction * d.reaction;
  for (const k of Object.keys(base.speeds)) {
    enemy.speeds[k] = base.speeds[k] * d.speed;
  }
  return d;
}
